import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { buildDirectUploadsUrl, convertApiUrlToHlsUrl } from '../utils/hlsUtils';

interface UseHLSPlayerProps {
  apiUrl: string; // Original API endpoint from response.answer
  contentType: 'video' | 'audio';
  autoplay?: boolean;
  controls?: boolean;
  muted?: boolean;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
  onError?: (error: any) => void;
}

export const useHLSPlayer = ({
  apiUrl,
  contentType,
  autoplay = false,
  controls = true,
  muted = false,
  onLoadStart,
  onLoadEnd,
  onError
}: UseHLSPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [streamUrl, setStreamUrl] = useState<string>('');
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [usingFallback, setUsingFallback] = useState(false);

  // 🔄 ENHANCED: Try HLS first, fallback to direct uploads URL on failure
  useEffect(() => {
    if (!apiUrl) return;

    // ⚡ Optimized fallback function to use direct CDN uploads URL
    const fallbackToDirectUploads = () => {
      console.log(`🔄 Falling back to direct CDN uploads URL...`);
      
      // Try to build direct uploads URL first
      const directUploadsUrl = buildDirectUploadsUrl(apiUrl, contentType);
      
      if (directUploadsUrl) {
        console.log(`⚡ Using direct uploads URL: ${directUploadsUrl}`);
        setUsingFallback(true);
        setStreamUrl(directUploadsUrl);
      } else {
        console.log(`📼 Could not build direct URL, using original API URL: ${apiUrl}`);
        setUsingFallback(true);
        setStreamUrl(apiUrl);
      }
      
      setIsLoading(false);
      onLoadEnd?.();
    };

    const tryHLSFirst = () => {
      try {
        setIsLoading(true);
        setUsingFallback(false);
        onLoadStart?.();

        // Step 1: Try to convert to HLS URL
        const hlsUrl = convertApiUrlToHlsUrl(apiUrl, contentType);
        
        if (hlsUrl && hlsUrl !== apiUrl) {
          console.log(`🎬 Attempting HLS playback: ${hlsUrl}`);
          setStreamUrl(hlsUrl);
        } else {
          // If conversion fails or returns same URL, use optimized fallback
          console.log(`🔄 HLS conversion failed, using optimized fallback...`);
          fallbackToDirectUploads();
        }
      } catch (error) {
        console.error('Error in HLS attempt, falling back:', error);
        fallbackToDirectUploads();
      }
    };

    tryHLSFirst();

    // Make fallbackToDirectUploads available to other effects
    (window as any).fallbackToDirectUploads = fallbackToDirectUploads;

    return () => {
      delete (window as any).fallbackToDirectUploads;
    };
  }, [apiUrl, contentType, onLoadStart, onLoadEnd]);

  // 🔄 ENHANCED: Initialize HLS or regular video based on URL type
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !streamUrl) return;

    // Clean up any existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const initializePlayer = () => {
      // If using fallback (direct uploads or API URL), use regular HTML5 video
      if (usingFallback) {
        console.log(`📼 Initializing HTML5 video with fallback URL: ${streamUrl}`);
        video.src = streamUrl;
        return;
      }

      // Otherwise try HLS
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
        });

        hlsRef.current = hls;
        hls.loadSource(streamUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log(`🎬 HLS ${contentType} manifest loaded successfully`);
          onLoadEnd?.();
          setIsLoading(false);
        });

        // 🔄 ENHANCED: Handle HLS errors with optimized fallback
        hls.on(Hls.Events.ERROR, (event: any, data: any) => {
          console.error('🚨 HLS Error:', data);
          
          // Check if it's a fatal network error (like 403/404)
          if (data.fatal && (data.type === 'networkError' || data.details === 'manifestLoadError')) {
            console.log(`🔄 HLS failed with ${data.details}, attempting optimized fallback...`);
            
            // Destroy HLS
            hls.destroy();
            hlsRef.current = null;
            
            // Use optimized fallback
            const fallbackFn = (window as any).fallbackToDirectUploads;
            if (fallbackFn) {
              fallbackFn();
            } else {
              // Backup fallback if window function not available
              setUsingFallback(true);
              setStreamUrl(apiUrl);
              setIsLoading(false);
              onLoadEnd?.();
            }
            
            return; // Don't call onError for fallback scenarios
          }
          
          // For non-fallback errors, still report them
          onError?.(data);
          setIsLoading(false);
        });

      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        console.log(`🍎 Using native HLS support: ${streamUrl}`);
        video.src = streamUrl;
        onLoadEnd?.();
        setIsLoading(false);
      } else {
        console.log('🚨 HLS not supported, falling back to optimized fallback');
        // Use optimized fallback for browsers that don't support HLS
        const fallbackFn = (window as any).fallbackToDirectUploads;
        if (fallbackFn) {
          fallbackFn();
        } else {
          // Backup fallback
          setUsingFallback(true);
          setStreamUrl(apiUrl);
        }
      }
    };

    initializePlayer();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [streamUrl, contentType, onLoadEnd, onError, usingFallback, apiUrl]);

  // Video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleDurationChange = () => setDuration(video.duration);
    const handleVolumeChange = () => setVolume(video.volume);

    // 🔄 ENHANCED: Handle video load errors for fallback scenarios
    const handleError = (e: Event) => {
      const error = (e.target as HTMLVideoElement).error;
      console.error(`🚨 Video element error:`, error);
      
      // If we're using fallback and still get an error, report it
      if (usingFallback) {
        onError?.(error);
      }
      setIsLoading(false);
    };

    const handleCanPlay = () => {
      if (usingFallback) {
        console.log(`📼 Fallback video ready to play: ${video.src}`);
        onLoadEnd?.();
      }
      setIsLoading(false);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('volumechange', handleVolumeChange);
    video.addEventListener('error', handleError);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('volumechange', handleVolumeChange);
      video.removeEventListener('error', handleError);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [usingFallback, onLoadEnd, onError]);

  const play = () => videoRef.current?.play();
  const pause = () => videoRef.current?.pause();
  const togglePlay = () => isPlaying ? pause() : play();

  return {
    videoRef,
    isPlaying,
    isLoading,
    duration,
    currentTime,
    volume,
    streamUrl,
    usingFallback, // 🔄 NEW: Expose whether we're using fallback
    controls: {
      play,
      pause,
      togglePlay,
    }
  };
};